import { TCreateAdminUserReqDto, TCreateAdminUserResDto } from "@/api/services/admin/admin-user/dtos/create-admin-user.dto";
import { TDeleteAdminUserResDto } from "@/api/services/admin/admin-user/dtos/delete-admin-user.dto";
import { TForceChangePasswordReqDto, TForceChangePasswordResDto } from "@/api/services/admin/admin-user/dtos/force-change-password.dto";
import { TGetAdminUserListReqDto, TGetAdminUserListResDto } from "@/api/services/admin/admin-user/dtos/get-admin-user-list.dto";
import { TGetAdminUserResDto } from "@/api/services/admin/admin-user/dtos/get-admin-user.dto";
import { TInitAdminUserReqDto, TInitAdminUserResDto } from "@/api/services/admin/admin-user/dtos/init-admin-user.dto";
import { TMintOauthRedirectReqDto, TMintOauthRedirectResDto } from "@/api/services/admin/admin-user/dtos/mint-oauth-redirect.dto";
import { TUpdateAdminUserReqDto, TUpdateAdminUserResDto } from "@/api/services/admin/admin-user/dtos/update-admin-user.dto";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { clientApi } from "@/utils/api";
import { createUrlWithParams } from "@/utils/url";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

// Provisions the product admin account (first login) WITHOUT minting a redirect. Idempotent.
// The redirect itself is minted later, on the /mint-oauth-redirect page, right before navigating —
// the handoff token only lives 60s server-side, so minting must stay close to the actual navigation.
export const initAdminUserApi = async (payload: TInitAdminUserReqDto): Promise<TInitAdminUserResDto> => {
  const { data } = await clientApi.post<TInitAdminUserResDto>("/admin-users/init", payload);
  return data;
};

// Mints the single-use SSO redirect URL. Self-heals by provisioning first when the admin has no
// product account yet, so entering the page directly still works.
export const mintOauthRedirectApi = async (payload: TMintOauthRedirectReqDto): Promise<TMintOauthRedirectResDto> => {
  const { data } = await clientApi.post<TMintOauthRedirectResDto>("/admin-users/mint-oauth-redirect", payload);
  return data;
};

export const createAdminUserApi = async (payload: TCreateAdminUserReqDto): Promise<TCreateAdminUserResDto> => {
  const rs = await clientApi.post<TCreateAdminUserResDto>("/admin-users", payload);
  return rs.data;
};

export const deleteAdminUserApi = async (id: number): Promise<TDeleteAdminUserResDto> => {
  const rs = await clientApi.delete<TDeleteAdminUserResDto>(`/admin-users/${id}`);
  return rs.data;
};

export const fetchAdminUserApi = async (id: string): Promise<TGetAdminUserResDto> => {
  const rs = await clientApi.get<TGetAdminUserResDto>(`/admin-users/${id}`);
  return rs.data;
};

export const useAdminUser = () => {
  const { id } = useParams();

  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_DETAIL, id],
    queryFn: () => (id ? fetchAdminUserApi(id) : undefined),
    enabled: Boolean(id),
  });
};

export const fetchAdminUserListApi = async (req: TGetAdminUserListReqDto) => {
  const url = createUrlWithParams("/admin-users", req);
  const rs = await clientApi.get<TGetAdminUserListResDto>(url);
  return rs.data;
};

export const useAdminUserList = (req: TGetAdminUserListReqDto) => {
  return useQuery({
    queryKey: [QUERY_KEYS.ADMIN_USER.FETCH_LIST, req],
    queryFn: () => fetchAdminUserListApi(req),
  });
};

export const updateAdminUserApi = async (userId: string, payload: TUpdateAdminUserReqDto): Promise<TUpdateAdminUserResDto> => {
  const rs = await clientApi.put<TUpdateAdminUserResDto>(`/admin-users/${userId}`, payload);
  return rs.data;
};

export const resendInvitationApi = async (id: number): Promise<{ data: true }> => {
  const rs = await clientApi.post<{ data: true }>(`/admin-users/${id}/resend-invitation`);
  return rs.data;
};

export const forceChangePasswordApi = async (payload: TForceChangePasswordReqDto): Promise<TForceChangePasswordResDto> => {
  const rs = await clientApi.post<TForceChangePasswordResDto>("/admin-users/force-change-password", payload);
  return rs.data;
};
